import Box from "@mui/material/Box";
import { tokens } from "../tokens";
import { useSampleData } from "../sample-data-context";
import { SkipLink } from "./SkipLink";

export interface PortalHeaderProps {
  /** AC-1: the business name, read through the renewal data port by
   * whatever screen supplies it — rendered at 600px (MUI's `sm`) and
   * above, on every screen except screen 1 (the sign-in route passes
   * nothing, the same "omitted means absent" contract `PortalLayout`'s
   * `helpLine` already uses). Never fabricated here: an absent value
   * renders nothing, never a placeholder string. */
  businessName?: string;
  /** H2 screens 1 and 2 draw the header with a 280px logo (170px on a phone) and slightly
   * different padding; every other screen uses the 240px logo. */
  variant?: "entry" | "flow";
}

/**
 * Shared portal header: the skip link (AC-1), the FRP logo
 * (`public/branding/frp-logo.svg`), the "Renewal portal" label
 * (ADR-20261008-hsvr) and, from TG-5, the business name. Rendered by
 * `PortalLayout`, so every view inside it carries the same header. The
 * logo is 170px wide on narrow viewports and 280px from `md` up, matching
 * the H2 design export's `logoWidth`.
 *
 * ADR-20261016-m7hx: padding is `10px 32px` at `md` and above (desktop) and a uniform `16px`
 * below it (narrow), per H2's README own "Global shell" section ("Header — white, 1px solid
 * #d6dfe6 bottom border; padding 10px 32px desktop, 16px ..."), replacing a prior, undocumented
 * uniform `16px 24px` that measured roughly 12-13px taller than H2's own reference screenshots on
 * every one of the 34 H2 states (m1-magic-link-portal-refresh's own AC-2 comparison, TG-8). No
 * other PortalHeader or PortalFooter behaviour changes.
 *
 * ADR-20261015-bzyr, narrowed by TG-4/ADR-20261016-6xax: also carries the
 * "Sample data" marker. Reads `useSampleData()`
 * (`design-system/sample-data-context.ts`), the composition root's own
 * `SampleDataContext` — set once, from the OR of every port App.tsx
 * bound this session's own `isSampleData` flag — rather than the former
 * `!import.meta.env.PROD` build-time proxy: now that
 * `createHttpRenewalSummaryPort` (a real, non-stub implementation) exists
 * alongside the remaining stub ports, "every screen in a non-production
 * build" is no longer the same set as "every stub-sourced screen", and
 * the marker must track the real signal, not the build mode. Rendered in
 * the header, not in `main`: see `PortalLayout.tsx`'s own doc comment for
 * why `main` cannot host it.
 */
export function PortalHeader({ businessName, variant }: Readonly<PortalHeaderProps>) {
  const entry = variant === "entry";
  const isSampleData = useSampleData();
  return (
    <Box
      component="header"
      sx={{
        position: "relative",
        backgroundColor: tokens.colour.neutral.white,
        borderBottom: `${tokens.interactionState.border.hairlineWidth}px solid ${tokens.colour.neutral.borderSubtle}`,
        // ADR-20261102-h2fd: H2's header on screens 3 to 8 (screens 1 and 2 differ slightly in H2
        // itself; one shared header carries the majority treatment).
        padding: entry
          ? { xs: `${tokens.spacing.md}px ${tokens.spacing.lg}px ${tokens.spacing.md}px ${tokens.spacing.md}px`, sm: `10px ${tokens.spacing.xl}px` }
          : // H2 screen 3 pads its header 14px top and bottom; screens 4 to 8 use 12px.
            { xs: `${tokens.spacing.md}px`, sm: variant === "flow" ? `14px ${tokens.spacing.xl}px` : `${tokens.spacing.sm}px ${tokens.spacing.xl}px` },
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: entry ? { xs: `${tokens.spacing.xxs}px`, sm: `${tokens.spacing.md}px` } : `${tokens.spacing.sm}px`,
      }}
    >
      <SkipLink />
      <Box
        component="img"
        src="/branding/frp-logo.svg"
        alt="Foundation Risk Partners"
        sx={{ width: entry ? { xs: 170, sm: 280 } : { xs: 150, sm: 240 }, height: "auto", display: "block", flexShrink: 0 }}
      />
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right", flex: "0 0 auto" }}>
        <Box
          component="span"
          sx={{
            color: tokens.colour.brand[700],
            fontSize: { xs: 17, sm: 19 },
            fontWeight: tokens.typography.weights.bold,
            lineHeight: 1.15,
            maxWidth: { xs: "5.5em", sm: "none" },
          }}
        >
          Renewal portal
        </Box>
        {businessName !== undefined ? (
          <Box
            component="span"
            sx={{
              display: { xs: "none", sm: "inline" },
              color: tokens.colour.brand[500],
              fontSize: 15,
            }}
          >
            {businessName}
          </Box>
        ) : null}
      </Box>
      {isSampleData ? (
        <Box
          component="span"
          data-testid="sample-data-marker"
          sx={{
            color: tokens.colour.neutral.inkMuted,
            fontSize: 13,
            fontWeight: tokens.typography.weights.regular,
          }}
        >
          Sample data
        </Box>
      ) : null}
    </Box>
  );
}
